import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchInboxAnnouncements,
  fetchInboxAnnouncementDetail,
  markAnnouncementRead,
  parseInboxAnnouncementsResult,
} from '../services/announcementsService';
import './DuyurularPage.css';
import TableListFooter from '../components/TableListFooter';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  return error?.message || error?.data?.mesaj || fallback;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const TechServiceDuyurularPage = () => {
  const { user } = useAuth();

  const [liste, setListe] = useState([]);
  const [sayfa, setSayfa] = useState(1);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [toplamKayit, setToplamKayit] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const [detay, setDetay] = useState(null);
  const [detayYukleme, setDetayYukleme] = useState(false);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata('');
    try {
      const res = await fetchInboxAnnouncements({ sayfa, limit: 25 });
      const { duyurular, sayfalama: p } = parseInboxAnnouncementsResult(res);
      setListe(duyurular);
      setToplamSayfa(p.toplamSayfa || 1);
      setToplamKayit(p.toplamKayit ?? 0);
    } catch (e) {
      setHata(getErrorMessage(e, 'Mesajlar yüklenemedi'));
      setListe([]);
    } finally {
      setYukleniyor(false);
    }
  }, [sayfa]);

  useEffect(() => {
    if (user?.rol !== 'tech-service') return;
    yukle();
  }, [yukle, user?.rol]);

  if (user?.rol === 'tech-person') {
    return <Navigate to="/teknisyen" replace />;
  }
  if (user?.rol !== 'tech-service') {
    return <Navigate to="/" replace />;
  }

  const acDetay = async (id) => {
    setDetay({ yukleniyor: true, veri: null });
    setDetayYukleme(true);
    try {
      await markAnnouncementRead(id);
      const res = await fetchInboxAnnouncementDetail(id);
      setDetay({ yukleniyor: false, veri: res?.veri || null });
      yukle();
    } catch (e) {
      setDetay(null);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'Açılamadı'));
      }
    } finally {
      setDetayYukleme(false);
    }
  };

  const okunduIsaretle = async (id) => {
    try {
      await markAnnouncementRead(id);
      if (window.showNotification) window.showNotification('success', 'Okundu olarak işaretlendi');
      yukle();
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'İşaretlenemedi'));
      }
    }
  };

  return (
    <main className="main-content tech-service-duyurular">
      <div className="content-header">
        <h1>Mesajlar</h1>
      </div>

      <div className="card">
        {hata && !yukleniyor && (
          <div className="error-state" style={{ margin: '16px' }}>
            {hata}
          </div>
        )}
        {yukleniyor ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Mesajlar yükleniyor</div>
          </div>
        ) : !hata && liste.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i
              className="fas fa-inbox"
              style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px', display: 'block' }}
              aria-hidden
            />
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Mesajınız yok.</div>
          </div>
        ) : hata && liste.length === 0 ? null : (
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Durum</th>
                  <th>Başlık</th>
                  <th>Tarih</th>
                  <th className="islemler-sutunu">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.okundu ? (
                        <span style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
                          <i className="fas fa-check-circle" style={{ color: '#22c55e' }} /> Okundu
                        </span>
                      ) : (
                        <span style={{ fontSize: '14px' }}>
                          <i className="fas fa-circle" style={{ color: '#94a3b8', fontSize: '0.55rem' }} /> Okunmadı
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="duyuru-title-link"
                        onClick={() => acDetay(d.id)}
                        style={{ fontSize: '14px' }}
                      >
                        {d.baslik}
                      </button>
                    </td>
                    <td>{formatDateTime(d.yayinAt)}</td>
                    <td className="table-actions islemler-sutunu">
                      {!d.okundu && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                          onClick={() => okunduIsaretle(d.id)}
                        >
                          Okundu işaretle
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!yukleniyor && (
        <TableListFooter
          totalRecords={toplamKayit}
          currentPage={sayfa}
          totalPages={toplamSayfa || 1}
          onPageChange={setSayfa}
          loading={yukleniyor}
        />
      )}

      {detay && (
        <div className="duyuru-modal-backdrop" role="presentation" onClick={() => setDetay(null)}>
          <div className="duyuru-modal duyuru-modal-wide" onClick={(e) => e.stopPropagation()}>
            {detay.yukleniyor || detayYukleme || !detay.veri ? (
              <p>Yükleniyor…</p>
            ) : (
              <>
                <h3>{detay.veri.duyuru?.baslik}</h3>
                <p className="duyuru-muted">{formatDateTime(detay.veri.duyuru?.yayinAt)}</p>
                <div className="duyuru-detail-body">{detay.veri.duyuru?.icerik}</div>
                <button type="button" className="btn-ghost" onClick={() => setDetay(null)}>
                  Kapat
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default TechServiceDuyurularPage;
